import React, { useState } from 'react';
import { render as renderMarkdown } from '../../utils/protectMarkdown';
import '../../styles/protect-markdown.css';
import { PAGE_TITLE } from './typeScale';

const VERDICT = {
  ALLOW: ['✓ Allowed', 'p7-status p7-status--allow'],
  DENY: ['Refused', 'p7-status p7-status--deny'],
  TRANSFORM: ['Transformed', 'p7-status p7-status--running'],
};

const EMPTY_DEFAULT = 'Nothing has been asked of it yet. Its first protected call will appear '
  + 'here with the verdict beside it.';

/**
 * Using the agent, with the verdict on every answer.
 *
 * <h3>Why the verdict sits under the reply</h3>
 *
 * The point is not that the agent answered; it is that the answer was allowed,
 * and on what terms. A transcript without the verdict is every other chat
 * window, and the one thing this product has to show is the thing those windows
 * cannot.
 *
 * <h3>An empty transcript is not a broken screen</h3>
 *
 * Nothing asked of it yet is a true and ordinary state for a freshly protected
 * agent, and it says so rather than rendering an empty box.
 *
 * <h3>What this component does not decide</h3>
 *
 * It paints the turns it is given and hands typed text back. Whether a call was
 * allowed, enforced or blocked is read from the platform's response by
 * `useTestRun`; which agents may be run at all is the model's own rule. Neither
 * is restated here.
 */
export default function ProtectRun({
  name, turns = [], ruleCount = null, live = true, busy = false,
  onRecord, onChange, onSend, composerDisabled = true,
  emptyHint = EMPTY_DEFAULT,
  disabledHint = 'Point your agent at the endpoint to run it from here',
}) {
  const [text, setText] = useState('');

  /* The renderer emits two controls — Copy on a code block, "Show all N rows" on
     a folded table. They are plain buttons in rendered markup, so the handler
     lives on the transcript and reads the button that was actually clicked. An
     emitted control nobody wired is worse than no control: it looks live and
     does nothing. */
  const onTranscriptClick = (e) => {
    const copy = e.target.closest?.('[data-md-copy]');
    if (copy) {
      const code = copy.closest('.md-pre')?.querySelector('code');
      if (code && navigator.clipboard) navigator.clipboard.writeText(code.textContent || '');
      return;
    }
    const more = e.target.closest?.('[data-md-more]');
    if (more) {
      const wrap = more.previousElementSibling;
      wrap?.querySelectorAll('.md-row-extra').forEach((row) => row.removeAttribute('hidden'));
      more.remove();
    }
  };

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || composerDisabled || !onSend) return;
    /* Cleared on hand-off rather than on reply: the box has to be usable again
       at once, and what was sent is already in the transcript. */
    setText('');
    onSend(trimmed);
  };

  return (
    <div style={{ minWidth: 0, width: '100%', maxWidth: '760px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                    marginBottom: 'var(--p7-space-4)' }}>
        <h1 style={PAGE_TITLE}>{name}</h1>
        {live ? <span className="p7-status p7-status--allow">Live</span> : null}
        {onRecord || onChange ? (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
            {onRecord ? (
              <button type="button" className="p7-btn p7-btn--sm" onClick={onRecord}>
                Trust &amp; record
              </button>
            ) : null}
            {onChange ? (
              <button type="button" className="p7-btn p7-btn--sm" onClick={onChange}>Change</button>
            ) : null}
          </span>
        ) : null}
      </div>

      <div className="p7-card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
                      padding: 'var(--p7-space-3) var(--p7-space-4)',
                      borderBottom: '1px solid var(--p7-line-soft)' }}>
          <span style={{ font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                         fontWeight: 600 }}>Today</span>
          {/* A number, or nothing. The chip used to default to zero, so a screen
              with no count yet — and a screen whose count could not be read —
              both stated that nothing was holding the agent. On the surface
              whose whole job is showing the boundary is live, that is the worst
              sentence it can say, and it was saying it by default. */}
          {Number.isFinite(ruleCount) ? (
            <span className="p7-status" style={{ marginLeft: 'auto' }}>
              {ruleCount} rule{ruleCount === 1 ? '' : 's'} on
            </span>
          ) : null}
        </div>

        {/* The handler sits on the transcript rather than on each emitted
            control, because those controls are markup the renderer produced and
            React never sees them as elements. Keyboard reaches them anyway:
            they are real <button> elements, so Enter and Space fire the same
            click this reads. */}
        <div onClick={onTranscriptClick}
             style={{ padding: 'var(--p7-space-4)', display: 'flex', flexDirection: 'column',
                      gap: 'var(--p7-space-4)', minHeight: '220px' }}>
          {turns.length === 0 ? (
            <p style={{ margin: 'auto', textAlign: 'center', maxWidth: '44ch',
                        font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)',
                        color: 'var(--p7-ink-subtle)' }}>
              {emptyHint}
            </p>
          ) : turns.map((t) => {
            const mine = t.who === 'you';
            const [label, cls] = VERDICT[t.verdict] || [];
            return (
              <div key={t.id} data-testid="turn" data-error={t.error ? 'true' : undefined}
                   style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '76%' }}>
                <div style={{ borderRadius: '12px', padding: '10px 14px',
                              font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)',
                              lineHeight: 1.5,
                              background: t.error ? 'var(--p7-deny-bg)'
                                : mine ? 'var(--p7-accent-bg)' : 'var(--p7-surface-sunken)',
                              border: t.error ? '1px solid var(--p7-deny)'
                                : mine ? 'none' : '1px solid var(--p7-line-soft)' }}>
                  {/* The agent's answer is C(k+1): the Protected Twin has already
                      scanned, ruled on and sealed it. What the twin does not do —
                      because it is not what it is for — is neutralise markup, so the
                      renderer escapes every leaf and emits only its own markup. Safe
                      by construction, which is why no trust claim is needed in either
                      direction.

                      The person's own words are never formatted: they did not ask for
                      markdown, and reinterpreting what somebody typed is the surface
                      editing them. A failed run is never formatted either — a failure
                      dressed as a well-set answer is worse than no answer. */}
                  {mine || t.error ? t.text : (
                    <div className="protect-md"
                         // eslint-disable-next-line react/no-danger
                         dangerouslySetInnerHTML={{ __html: renderMarkdown(t.text) }} />
                  )}
                </div>
                {/* A failed run that reads like an answer is worse than no
                    answer, so it is marked and never carries a verdict. */}
                {t.error ? (
                  <div style={{ marginTop: '6px' }}>
                    <span className="p7-status p7-status--deny">Did not run</span>
                  </div>
                ) : label || t.notes?.length || t.rule ? (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px',
                                flexWrap: 'wrap' }}>
                    {label ? <span className={cls}>{label}</span> : null}
                    {(t.notes || []).map((n) => <span key={n} className="p7-status">{n}</span>)}
                    {t.rule ? <span className="p7-status">Rule · {t.rule}</span> : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: '9px', padding: 'var(--p7-space-3)',
                      borderTop: '1px solid var(--p7-line-soft)',
                      background: 'var(--p7-surface-sunken)' }}>
          <input
            type="text"
            aria-label="Ask it something"
            value={text}
            placeholder={composerDisabled ? disabledHint : 'Ask it something…'}
            disabled={composerDisabled}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
            style={{ flex: 1, minWidth: 0, padding: '10px 13px',
                     border: '1px solid var(--p7-line-strong)',
                     borderRadius: 'var(--p7-radius-control)',
                     font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)' }}
          />
          <button type="button" className="p7-btn p7-btn--primary"
                  disabled={composerDisabled || !text.trim()} onClick={submit}>
            {busy ? 'Running…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
